import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Settings, Save, Key, Bell, Shield, Palette } from 'lucide-react';

export const SettingsPage = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState({
    defaultPrefix: 'story!',
    defaultLanguage: 'storybook',
    notifications: {
      enabled: true,
      mentions: true,
      errors: false
    },
    privacy: {
      showAvatar: true,
      showStats: true,
      allowSearch: false
    },
    api: {
      regenerateKey: false
    }
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleSave = async () => {
    setLoading(true);
    setMessage('');
    
    // Mock save operation - replace with actual API call
    setTimeout(() => {
      setLoading(false);
      setMessage('Settings saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    }, 1000);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white mb-2">Settings</h1>
        <p className="text-discord-light">
          Manage your Storybook bot preferences and account settings.
        </p>
      </div>

      {/* Account Settings */}
      <div className="discord-card">
        <div className="flex items-center space-x-2 mb-6">
          <Settings className="w-5 h-5 text-discord-blurple" />
          <h2 className="text-xl font-semibold text-white">Account Settings</h2>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Default Prefix
              </label>
              <p className="text-xs text-discord-light">
                Default command prefix for your servers
              </p>
            </div>
            <input
              type="text"
              value={settings.defaultPrefix}
              onChange={(e) => setSettings({...settings, defaultPrefix: e.target.value})}
              className="discord-input w-32"
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Default Language
              </label>
              <p className="text-xs text-discord-light">
                Language for bot responses
              </p>
            </div>
            <select
              value={settings.defaultLanguage}
              onChange={(e) => setSettings({...settings, defaultLanguage: e.target.value})}
              className="discord-input w-32"
            >
              <option value="storybook">Storybook</option>
              <option value="english">English</option>
              <option value="spanish">Spanish</option>
            </select>
          </div>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="discord-card">
        <div className="flex items-center space-x-2 mb-6">
          <Bell className="w-5 h-5 text-discord-blurple" />
          <h2 className="text-xl font-semibold text-white">Notifications</h2>
        </div>

        <div className="space-y-4">
          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <span className="text-white">Enable Notifications</span>
              <p className="text-xs text-discord-light">Receive desktop notifications</p>
            </div>
            <input
              type="checkbox"
              checked={settings.notifications.enabled}
              onChange={(e) => setSettings({
                ...settings, 
                notifications: {...settings.notifications, enabled: e.target.checked}
              })}
              className="w-4 h-4 text-discord-blurple rounded focus:ring-discord-blurple"
            />
          </label>

          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <span className="text-white">Mention Notifications</span>
              <p className="text-xs text-discord-light">Notify when characters are mentioned</p>
            </div>
            <input
              type="checkbox"
              checked={settings.notifications.mentions}
              onChange={(e) => setSettings({
                ...settings, 
                notifications: {...settings.notifications, mentions: e.target.checked}
              })}
              className="w-4 h-4 text-discord-blurple rounded focus:ring-discord-blurple"
            />
          </label>

          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <span className="text-white">Error Notifications</span>
              <p className="text-xs text-discord-light">Notify when errors occur</p>
            </div>
            <input
              type="checkbox"
              checked={settings.notifications.errors}
              onChange={(e) => setSettings({
                ...settings, 
                notifications: {...settings.notifications, errors: e.target.checked}
              })}
              className="w-4 h-4 text-discord-blurple rounded focus:ring-discord-blurple"
            />
          </label>
        </div>
      </div>

      {/* Privacy Settings */}
      <div className="discord-card">
        <div className="flex items-center space-x-2 mb-6">
          <Shield className="w-5 h-5 text-discord-blurple" />
          <h2 className="text-xl font-semibold text-white">Privacy</h2>
        </div>

        <div className="space-y-4">
          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <span className="text-white">Show Avatar</span>
              <p className="text-xs text-discord-light">Display your avatar on public profiles</p>
            </div>
            <input
              type="checkbox"
              checked={settings.privacy.showAvatar}
              onChange={(e) => setSettings({
                ...settings, 
                privacy: {...settings.privacy, showAvatar: e.target.checked}
              })}
              className="w-4 h-4 text-discord-blurple rounded focus:ring-discord-blurple"
            />
          </label>

          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <span className="text-white">Show Statistics</span>
              <p className="text-xs text-discord-light">Display message counts and stats</p>
            </div>
            <input
              type="checkbox"
              checked={settings.privacy.showStats}
              onChange={(e) => setSettings({
                ...settings, 
                privacy: {...settings.privacy, showStats: e.target.checked}
              })}
              className="w-4 h-4 text-discord-blurple rounded focus:ring-discord-blurple"
            />
          </label>

          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <span className="text-white">Allow Search</span>
              <p className="text-xs text-discord-light">Allow others to find your characters</p>
            </div>
            <input
              type="checkbox"
              checked={settings.privacy.allowSearch}
              onChange={(e) => setSettings({
                ...settings, 
                privacy: {...settings.privacy, allowSearch: e.target.checked}
              })}
              className="w-4 h-4 text-discord-blurple rounded focus:ring-discord-blurple"
            />
          </label>
        </div>
      </div>

      {/* API Settings */}
      <div className="discord-card">
        <div className="flex items-center space-x-2 mb-6">
          <Key className="w-5 h-5 text-discord-blurple" />
          <h2 className="text-xl font-semibold text-white">API Access</h2>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white mb-1">
              API Key
            </label>
            <div className="flex space-x-2">
              <input
                type="password"
                value="sk-xxxxxxxxxxxxxxxxxxxxxxxx"
                readOnly
                className="discord-input flex-1"
              />
              <button className="discord-button">
                Copy
              </button>
            </div>
          </div>

          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <span className="text-white">Regenerate API Key</span>
              <p className="text-xs text-discord-light">Create a new API key (invalidates current one)</p>
            </div>
            <input
              type="checkbox"
              checked={settings.api.regenerateKey}
              onChange={(e) => setSettings({
                ...settings, 
                api: {...settings.api, regenerateKey: e.target.checked}
              })}
              className="w-4 h-4 text-discord-blurple rounded focus:ring-discord-blurple"
            />
          </label>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex items-center justify-between">
        {message && (
          <div className={`text-sm ${
            message.includes('success') ? 'text-discord-green' : 'text-discord-red'
          }`}>
            {message}
          </div>
        )}
        
        <button
          onClick={handleSave}
          disabled={loading}
          className="discord-button flex items-center space-x-2"
        >
          <Save className="w-4 h-4" />
          <span>{loading ? 'Saving...' : 'Save Settings'}</span>
        </button>
      </div>
    </div>
  );
};
